import { Component, OnDestroy, OnInit } from '@angular/core';
import { LeftMenu } from '@shared/models/left-menu.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { environment } from '@environment';
import { Subject } from 'rxjs';
import { CommonDataService } from '@sb-tools/typescript/services/common-data.service';
import { UserRole } from '@sb-tools/typescript/enums/user-role.enum';
import { CommunitySwitcherComponent } from '@shared/components/community-switcher/community-switcher.component';
import { NgClass } from '@angular/common';
import { TruncatePipe } from '@sb-tools/typescript/pipes/truncate.pipe';
import { LeftMenuBlockComponent } from '@shared/components/left-menu/left-menu-block/left-menu-block.component';

@Component({
    selector: 'app-left-menu',
    templateUrl: './left-menu.component.html',
    styleUrls: ['./left-menu.component.scss'],
    imports: [CommunitySwitcherComponent, NgClass, TranslatePipe, TruncatePipe, LeftMenuBlockComponent],
})
export class LeftMenuComponent implements OnInit, OnDestroy {
    communityMenu: LeftMenu;
    superAdminMenu: LeftMenu;
    userMenu: LeftMenu;

    private communityName: string;
    private unsubscriber = new Subject<void>();

    constructor(
        private commonDataService: CommonDataService,
        private translateService: TranslateService,
    ) {}

    ngOnInit() {
        this.communityName = this.commonDataService.communityName;

        this.initUserMenu();

        if (
            this.commonDataService.user.isAdmin ||
            this.commonDataService.user.isSystemAdmin ||
            this.commonDataService.user.hasAccessToFAQ ||
            this.commonDataService.user.role === UserRole.manager
        ) {
            this.initCommunityMenu();
        }

        if (this.commonDataService.user.constraints?.isExtendedAccessEnabled) {
            this.initSuperAdminMenu();
        }
    }

    private initUserMenu() {
        this.userMenu = {
            title: this.translateService.instant('Your Settings'),
            icon: 'fa fa-user',
            items: [
                {
                    title: this.translateService.instant('General'),
                    icon: 'fa fa-cog',
                    level: 1,
                    routerLink: `/${this.communityName}/user/settings`,
                },
            ],
        };

        if (
            this.commonDataService.user.role !== UserRole.readOnlyUser &&
            this.commonDataService.user.role !== UserRole.systemAdmin
        ) {
            this.userMenu.items.push(
                {
                    title: this.translateService.instant('Filters'),
                    icon: 'fa fa-filter',
                    level: 1,
                    routerLink: `/${this.communityName}/user/filters`,
                },
                {
                    title: this.translateService.instant('Personal templates'),
                    icon: 'fa fa-reply',
                    level: 1,
                    routerLink: `/${this.communityName}/user/personal-templates`,
                },
            );
        }
    }

    private initCommunityMenu() {
        this.communityMenu = {
            title: this.translateService.instant('Settings for') + ' ' + this.communityName,
            icon: 'fa fa-building',
            items: [],
        };

        if (this.commonDataService.user.isSystemAdmin || this.commonDataService.user.isAdmin) {
            this.communityMenu.items = [
                {
                    title: this.translateService.instant('General'),
                    icon: 'fa fa-cog',
                    level: 1,
                    routerLink: `/${this.communityName}/community/settings`,
                },
                {
                    title: this.translateService.instant('Teammates'),
                    icon: 'fa fa-users',
                    level: 1,
                    routerLink: `/${this.communityName}/community/teammates`,
                    children: [
                        {
                            title: this.translateService.instant('Users'),
                            icon: 'fa fa-user',
                            level: 2,
                            routerLink: `/${this.communityName}/community/teammates/users`,
                        },
                        {
                            title: this.translateService.instant('Groups'),
                            icon: 'fas fa-user-friends',
                            level: 2,
                            routerLink: `/${this.communityName}/community/teammates/groups`,
                        },
                    ],
                },
                {
                    title: this.translateService.instant('Filters'),
                    icon: 'fas fa-filter',
                    level: 1,
                    routerLink: `/${this.communityName}/community/filters`,
                },
                {
                    title: this.translateService.instant('Contacts'),
                    icon: 'fas fa-address-book',
                    level: 1,
                    routerLink: `/${this.communityName}/community/contacts`,
                },
                {
                    title: this.translateService.instant('Categories'),
                    icon: 'fa fa-folder',
                    level: 1,
                    routerLink: `/${this.communityName}/community/categories`,
                },
                {
                    title: this.translateService.instant('Tags'),
                    icon: 'fa fa-tag',
                    level: 1,
                    routerLink: `/${this.communityName}/community/tags`,
                },
                {
                    title: this.translateService.instant('Reply templates'),
                    icon: 'fa fa-reply',
                    level: 1,
                    routerLink: `/${this.communityName}/community/reply-templates`,
                },
                {
                    title: this.translateService.instant('Email templates'),
                    icon: 'fa fa-envelope',
                    level: 1,
                    routerLink: `/${this.communityName}/community/email-templates`,
                },
                {
                    title: this.translateService.instant('Fields library'),
                    icon: 'fas fa-pencil-alt',
                    level: 1,
                    routerLink: `/${this.communityName}/community/fields-library`,
                },
                {
                    title: this.translateService.instant('Forms'),
                    icon: 'fab fa-wpforms',
                    level: 1,
                    routerLink: `/${this.communityName}/community/forms`,
                },
                {
                    title: this.translateService.instant('Widgets'),
                    icon: 'fas fa-comment',
                    level: 1,
                    routerLink: `/${this.communityName}/community/widgets`,
                },
                {
                    title: this.translateService.instant('Spamfilter'),
                    icon: 'fa fa-ban',
                    level: 1,
                    routerLink: `/${this.communityName}/community/spammers`,
                },
                {
                    title: this.translateService.instant('Email bounces'),
                    icon: 'fa fa-envelope',
                    level: 1,
                    routerLink: `/${this.communityName}/community/email-bounces`,
                },
                // {
                //     title: this.translateService.instant('Email complaints'),
                //     icon: 'fa fa-envelope',
                //     level: 1,
                //     routerLink: `/${this.communityName}/community/email-complaints`,
                // },
                {
                    title: this.translateService.instant('Installation'),
                    icon: 'fas fa-code',
                    level: 1,
                    routerLink: `/${this.communityName}/community/installation`,
                },
                {
                    title: this.translateService.instant('Integration'),
                    icon: 'fas fa-compress-alt',
                    level: 1,
                    routerLink: `/${this.communityName}/community/integration`,
                },
                {
                    title: this.translateService.instant('Rules'),
                    icon: 'fas fa-book',
                    level: 1,
                    routerLink: `/${this.communityName}/community/rulesEngine`,
                },
                {
                    title:
                        this.communityName[0].toUpperCase() +
                        this.communityName.slice(1) +
                        ' ' +
                        this.translateService.instant('FAQ'),
                    icon: 'fa fa-question',
                    level: 1,
                    routerLink: environment.faqUrl + this.communityName + '/admin',
                    isExternalLink: true,
                },
                {
                    title: this.translateService.instant('Experimental'),
                    icon: 'fas fa-flask',
                    level: 1,
                    routerLink: `/${this.communityName}/community/experimental`,
                },
                {
                    title: this.translateService.instant('Entra ID'),
                    icon: 'fas fa-id-card',
                    level: 1,
                    routerLink: `/${this.communityName}/community/entra-id`,
                },
                {
                    title: this.translateService.instant('Activity log'),
                    icon: 'fas fa-list-ul',
                    level: 1,
                    routerLink: `/${this.communityName}/community/activity-log`,
                    sortIndex: 1000,
                },
            ];
        }

        if (this.commonDataService.user.role === UserRole.manager) {
            this.communityMenu.items.push(
                {
                    title: this.translateService.instant('Tags'),
                    icon: 'fa fa-tag',
                    level: 1,
                    routerLink: `/${this.communityName}/community/tags`,
                },
                {
                    title: this.translateService.instant('Reply templates'),
                    icon: 'fa fa-reply',
                    level: 1,
                    routerLink: `/${this.communityName}/community/reply-templates`,
                },
                {
                    title: this.translateService.instant('Widgets'),
                    icon: 'fas fa-comment',
                    level: 1,
                    routerLink: `/${this.communityName}/community/widgets`,
                },
            );
        }

        if (!this.commonDataService.user.isAdmin && !this.commonDataService.user.isSystemAdmin) {
            this.communityMenu.items.push({
                title:
                    this.communityName[0].toUpperCase() +
                    this.communityName.slice(1) +
                    ' ' +
                    this.translateService.instant('FAQ'),
                icon: 'fa fa-question',
                level: 1,
                routerLink: environment.faqUrl + this.communityName + '/admin',
                isExternalLink: true,
            });
        }

        if (
            (this.commonDataService.user.role === UserRole.admin ||
                this.commonDataService.user.role === UserRole.systemAdmin) &&
            this.commonDataService.user.hasAccessToGDPR
        ) {
            this.communityMenu.items.push({
                title: this.translateService.instant('Access request portal'),
                icon: 'fas fa-shield-alt',
                level: 1,
                routerLink: `/${this.communityName}/community/access-request-portal`,
            });
        }

        if (
            ['socialboards', 'zissonas', 'demo', 'nastiacom', 'devnastia4', 'testz', 'longnicenamefortest'].includes(
                this.communityName?.toLowerCase(),
            ) &&
            (this.commonDataService.user.isAdmin || this.commonDataService.user.isSystemAdmin)
        ) {
            this.communityMenu.items.push({
                title: this.translateService.instant('Applications'),
                icon: 'fas fa-th-large',
                level: 1,
                routerLink: `/${this.communityName}/community/applications`,
            });
        }

        this.communityMenu.items.sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0));
    }

    private initSuperAdminMenu() {
        this.superAdminMenu = {
            title: this.translateService.instant('Super Admin'),
            icon: 'fa fa-user-circle',
            items: [
                {
                    title: this.translateService.instant('Community statistics'),
                    icon: 'fas fa-chart-area',
                    level: 1,
                    routerLink: `/${this.communityName}/super-admin/community-statistics`,
                },
                {
                    title: this.translateService.instant('Social media account statuses'),
                    icon: 'fa fa-check-circle',
                    level: 1,
                    routerLink: `/${this.communityName}/super-admin/social-media-account-statuses`,
                },
                {
                    title: this.translateService.instant('Trials'),
                    icon: 'fa fa-clock',
                    level: 1,
                    routerLink: `/${this.communityName}/super-admin/trials`,
                },
                {
                    title: this.translateService.instant('Sentiment analysis'),
                    icon: 'fa fa-smile',
                    level: 1,
                    routerLink: `/${this.communityName}/super-admin/sentiment-analysis`,
                },
                {
                    title: this.translateService.instant('Ticket anonymization'),
                    icon: 'fas fa-mask',
                    level: 1,
                    routerLink: `/${this.communityName}/super-admin/ticket-anonymization`,
                },
            ],
        };
    }

    ngOnDestroy() {
        this.unsubscriber.next();
        this.unsubscriber.complete();
    }
}
